/**
 * The tab's runtime: one clock, one pooled HTTP connection, one socket.
 *
 * Phases 0–3 built every piece of this and wired none of them together —
 * `app/boot.ts` had no caller. This is the caller, and it owns the three
 * singletons D8 and plan §2.1 allow the tab to have. Nothing else constructs an
 * `HttpClient`, a `WsClient` or a `Clock`.
 *
 * ```
 * boot()  ─▶ seed the stores from the REST snapshot   ─▶ FIRST PAINT
 *   │                                                        │
 *   └─▶ config.ws ─▶ WsClient ─▶ one batched subscribe ─▶ in-band snapshots
 * ```
 *
 * **Panels read the stores, never the REST bodies.** The cold-load response is
 * applied through `StoreSet.apply` on the same path the socket's own snapshot
 * takes, so there is exactly one render source and no component has to know
 * whether the socket has landed yet. That is components.md §4.1 — "every panel
 * renders from an HTTP snapshot first, then applies WS deltas" — implemented as
 * one code path rather than as two branches in every panel.
 *
 * **Nothing waits on the socket** (§5.1). If the WebSocket never opens the page
 * is a complete, correct, static snapshot behind a `STALE` pill. `boot()` does
 * not know the socket exists; this file connects it afterwards.
 *
 * **The market page has no HTTP timer.** After boot the only network calls are
 * the clock's 15-minute backstop and the keep-warm `HEAD /time`, both of which
 * `docs/03` Appendix B accounts for. Re-targeting at an anchor boundary is a
 * *subscription* change and costs zero requests — see `retarget` below.
 */

import { HttpClient } from '@/api/http/client'
import { KeepWarm } from '@/api/http/keep-warm'
import { createApi, type Api } from '@/api/http/endpoints'
import { WsClient } from '@/api/ws/client'
import { resolveWsConfig, type ConnectionStatus } from '@/api/ws/connection'
import { depthChannelsFor, PRIVATE_CHANNELS } from '@/api/ws/channels'
import type { ChannelFrame, ChannelName, HeartbeatData, WsErrorCode } from '@/api/ws/protocol'
import {
  ClockSyncScheduler,
  sharedClock,
  WAKE_RESYNC_THRESHOLD_MS,
  type Clock,
  type ClockStatus,
} from '@/lib/clock'
import {
  ALL_ASSETS,
  servedAssets,
  anchorAt,
  assetForSymbol,
  buildMarketId,
  seriesIdFor,
  symbolFor,
  type Asset,
  type Symbol_,
} from '@/lib/market-id'
import { formatFixed, parseFixed, USDC_DP } from '@/lib/decimal'
import { formatSignedUsdc } from '@/lib/format'
import { ExternalStore } from '@/stores/store'
import { pushToast, requestBottomTab } from '@/stores/ui'
import type { StoreSet } from '@/stores'
// `wallet/identity` is deliberately viem-free — it imports only `stores/store`
// and two type-only modules — so the header can subscribe to identity state at
// cold load without pulling the wallet chunk. `wallet/controller` is *not*
// viem-free and is therefore imported dynamically, below.
import { IdentityStore } from '@/wallet/identity'
import type { IdentityController } from '@/wallet/controller'
import { readTradingLimits, type TradingLimits } from '@/domain/order-config'
import { verifySigningDomain } from '@/domain/chain-trust'
import { connectionClockAfter } from '@/domain/connection-notice'
import { targetsFrom } from '@/wallet/chain-targets'
import { APP_VERSION, CHAIN_PINS } from '@/lib/env'
import { buildIsTooOld } from '@/domain/client-build'
import { OrderController } from '@/trading/order-controller'
import type { ErrorAction } from '@/domain/order-errors'
import {
  drawRollJitter,
  rollHolds,
  type RollEventKind,
  type RollHolds,
  type RollJitter,
  type RollTiming,
  watchdogReconciles,
} from '@/domain/roll'
import { RollScheduler } from './roll-scheduler'
import {
  boot,
  timeFetcherFor,
  STRIP_INCLUDE,
  type BootFailure,
  type BootResult,
  type PrivateSnapshot,
} from './boot'

export type RuntimePhase = 'IDLE' | 'BOOTING' | 'READY' | 'FAILED'

/**
 * What the page needs that is not in a channel store.
 *
 * Deliberately small. Anything a socket delivers lives in `StoreSet` and is read
 * through `useStore`; this is the boot-shaped residue — which asset is focused,
 * whether the cold load finished, and whether the socket is up.
 */
export interface RuntimeState {
  phase: RuntimePhase
  asset: Asset
  /** What the venue runs, from `/config.symbols` (§F138). Stable once READY. */
  assets: readonly Asset[]
  symbol: Symbol_
  /** The anchor the page is pointed at, in seconds. */
  anchorS: number
  marketId: string
  seriesId: string
  /**
   * The market that expired at the current anchor and is resolving behind the
   * one on screen — the **secondary surface**'s subject (docs/03 §6.3).
   *
   * `null` outside the hold window, which runs from the boundary to `T+90`. It
   * is the *identity* only: `<ResolutionPanel>` subscribes `market.{id}` itself,
   * because a store subscribed by the screen re-renders the screen (invariant
   * 2). Two markets are on screen for thirteen seconds of every sixty, and the
   * tradable one owns the primary column at all times.
   */
  resolvingMarketId: string | null
  connection: ConnectionStatus
  /**
   * The socket has been `OPEN` and then not, at least once in this tab's life.
   *
   * The banner needs it and cannot derive it: `Reconnected.` and a first
   * successful cold load are the same `(status, statusForMs)` pair, and telling
   * a user they have reconnected to something they were never disconnected from
   * reads as a failure they missed (`docs/07` §3).
   */
  resumed: boolean
  /**
   * When the socket last came up or went down, on `performance.now()`'s
   * monotonic scale — not when `connection` last changed: `BACKOFF` and
   * `CONNECTING` are one outage (`connectionClockAfter`).
   *
   * Measured here rather than in the banner because this is where the
   * transition happens, and because a duration held in a component is a duration
   * that resets when the component remounts — the banner is mounted per route
   * and a route change would restart the two-second clock mid-reconnect.
   *
   * Monotonic, not `clock.now()`: a wake re-sync can move the corrected server
   * clock by more than the threshold it is being compared against.
   */
  connectionSinceMs: number
  /** §9.2.3. The session behind the socket expired and this tab went anonymous
   *  on its own word. Cleared when a session comes back. */
  sessionExpired: boolean
  /**
   * The venue's signing configuration is one this client will not sign against
   * (§F48e), or `null` when it is fine.
   *
   * Held here rather than in the wallet chunk because the **ticket** is what
   * has to say so, and the ticket renders for a visitor whose wallet stack has
   * never loaded. `null` is also the honest value before `ensureIdentity()` has
   * run: nothing has been checked, and nothing is being signed either.
   */
  signingBlocked: { code: string; detail: string } | null
  /** `heartbeat.venue_status`, rolled up from `GET /health`. `null` until a beat
   *  carries one — never assumed healthy. */
  venueStatus: NonNullable<HeartbeatData['venue_status']> | null
  /** The last terminal `error` frame's code, cleared by a clean reconnect. The
   *  banner's only input that is not a status (§9.2.7, §9.2.11). */
  lastProblem: WsErrorCode | null
  /**
   * `heartbeat.connection_id` — "quote it in support tickets; it is in every
   * server log line".
   *
   * Rendered nowhere yet. It is here because it is the only handle a browser
   * has on *its own* socket, which is what lets the resilience suite stage a
   * fault against one connection instead of against every tab the mock is
   * serving (`e2e/resilience.spec.ts`).
   */
  connectionId: string | null
  /** `navigator.onLine`. §9.2.6 — a captive portal is not distinguishable from
   *  this, which is why it is a hint for copy and never a gate on a request. */
  online: boolean
  /**
   * What settled while this tab was disconnected, collapsed into one line.
   *
   * §9.2.5 step 4: **never replay the receipt animation for a payout the user
   * did not watch.** Six receipts in sequence after a reconnect is noise that
   * looks like a bug, so the reconciliation summarises instead — and the
   * durable record was always the Settled tab.
   */
  awayMarkets: number
  clock: ClockStatus
  /** `null` until `/config` lands. Nothing in `config.ws` is a constant. */
  periodS: number
  error: unknown
  /** Non-fatal cold-load failures, for the `STALE`/degraded surfaces. */
  failures: { step: string; error: unknown }[]
  bootedAtMs: number | null
}

const DEFAULT_PERIOD_S = 60

/**
 * How long a tab may be hidden before its depth channels are dropped (§9.2.4).
 *
 * Not a `config.ws` value — it is a client-side battery decision rather than a
 * transport parameter, and `docs/03` §9.2.4 states the number in prose. Nothing
 * else about the hidden tab changes: the ping cadence is `config.ws`'s and the
 * private channels are kept precisely so notifications and settlements land.
 */
const HIDDEN_DEPTH_DROP_MS = 90_000
/**
 * The quiet window either side of a market boundary (§6.0 Fact 2).
 *
 * One second, which is comfortably wider than the clock error the correction
 * leaves and narrow enough that a gap detected outside it is repaired at once.
 * `Runtime#boundaryHoldMs` is the only reader.
 */
const BOUNDARY_QUIET_MS = 1_000

export interface RuntimeOptions {
  clock?: Clock
  /** Injected by tests. Production passes nothing and gets `window.fetch`. */
  fetchImpl?: typeof fetch
  /** Skip the socket entirely — the "WS never opens" path of §9.2.1. */
  connectSocket?: boolean
  clockSamples?: number
  clockSpreadMs?: number
  /** Seeded by tests, which cannot assert against `U(0,8)s`. Production draws
   *  once per tab per session — never per roll (docs/03 §6.1 item 4). */
  rollJitter?: RollJitter
}

export class Runtime {
  readonly clock: Clock
  readonly http: HttpClient
  readonly api: Api
  readonly state = new ExternalStore<RuntimeState>({
    phase: 'IDLE',
    asset: 'btc',
    assets: ['btc'],
    symbol: 'BTC-USD',
    anchorS: 0,
    marketId: '',
    seriesId: 'btc-updown-1m',
    resolvingMarketId: null,
    connection: 'IDLE',
    resumed: false,
    connectionSinceMs: 0,
    sessionExpired: false,
    signingBlocked: null,
    venueStatus: null,
    lastProblem: null,
    connectionId: null,
    // Optimistic on purpose: a browser with no `navigator.onLine` — and a
    // headless harness — is online until something says otherwise. A false
    // "You're offline." over a working connection is the worse error.
    online: true,
    awayMarkets: 0,
    clock: 'UNSYNCED',
    periodS: DEFAULT_PERIOD_S,
    error: null,
    failures: [],
    bootedAtMs: null,
  })

  /**
   * Identity state, readable from cold load. The *controller* is lazy; this is
   * not, because `<ConnectButton>` subscribes to it on the anonymous market page
   * and a store that did not exist yet would mean a second render path for the
   * five seconds before the wallet chunk lands.
   */
  readonly identityStore = new IdentityStore()

  #ws: WsClient | null = null
  #keepWarm: KeepWarm | null = null
  #clockScheduler: ClockSyncScheduler | null = null
  #booted: BootResult | null = null
  #identity: IdentityController | null = null
  #identityLoading: Promise<IdentityController> | null = null
  #connectSocket: boolean
  #options: RuntimeOptions
  #starting: Promise<void> | null = null
  /**
   * The roll (Phase 7). One class, five instants, one timer field — §4.3's
   * decision, taken as `docs/10` §0.5 recommends.
   *
   * It replaced `#scheduleAnchorFollow`, which was the swap and nothing else and
   * whose own comment said so. The property that comment insisted on is kept:
   * the wake-up is re-derived from the *corrected* clock every tick rather than
   * running as an interval, because an interval drifts against the server and
   * the point is to turn over when the server says the anchor did.
   */
  #roll: RollScheduler | null = null
  readonly #rollJitter: RollJitter
  /** What the tab holds beyond the focused market, as anchors. Derived from the
   *  corrected clock on every roll event — see `#applyHolds` and `rollHolds`. */
  #holds: RollHolds = { depth: [], markets: [] }
  /**
   * The previous asset's `ticker`, retained across a switch.
   *
   * §5.5 keeps the departing asset's `market` and `ticker` subscribed so the
   * strip keeps showing its price and switching back is free. `market.*` for
   * both assets is in the base set already; this is the other half.
   */
  #retainedTicker: ChannelName | null = null

  /**
   * Whether the six private channels are in the desired set.
   *
   * Phase 5 authenticated the socket and added no channels, because nothing in
   * Phase 5 rendered private data and a subscription with no consumer is budget
   * spent for nothing. Phase 6 has the consumers, so this flips on when a
   * session resolves and off at logout — and each flip is **one batched frame**,
   * because `setDesired` diffs the whole set and sends one `subscribe` and one
   * `unsubscribe` (docs/03 §5.3).
   */
  #privateChannels = false
  /** The socket has opened at least once. `resumed` is the *second* open. */
  #everOpen = false
  #hidden = false
  /** The market on screen when the tab went hidden — docs/03 §9.2.4 step 7's
   *  own condition, recorded so `#onWake` can compare it rather than guess
   *  from a duration. */
  #hiddenAtMarketId: string | null = null
  #depthSuspended = false
  #depthTimer: number | null = null
  #networkUnsubscribe: (() => void) | null = null
  #reconciling: Promise<void> | null = null
  /**
   * The corrected-clock instant the last reconciliation covered up to, and the
   * `since_ms` / `start_ms` bound of the next one.
   *
   * `null` before the first, which is the only time an unbounded read is
   * correct — the cold load's own wave has no gap behind it to fill.
   */
  /**
   * The bound on every reconciliation fetch, and it is seeded at **boot**.
   *
   * `null` means "since the beginning of time", which is what `GET
   * /portfolio/settlements` was asked for on the first reconcile of every
   * session — so the first alt-tab of a session pulled up to 25 settlements
   * from the user's whole history and toasted *"While you were away — 25
   * markets settled"* about markets that resolved yesterday. The user was not
   * away; the tab had been open for five seconds (`docs/17`).
   *
   * Seeded from `bootedAtMs`, because the cold load has already fetched the
   * account: everything before that instant is on screen, and "while you were
   * away" is a statement about a gap in *this* tab's stream. Settlements from
   * between sessions are still in the Settled tab, which is the durable record
   * — §9.2.5's own rule is *never animate a payout the user did not watch*, and
   * a payout from last night is the clearest case of one.
   */
  #reconciledToMs: number | null = null
  #identityUnsubscribe: (() => void) | null = null
  #trading: OrderController | null = null
  #onErrorAction: ((action: ErrorAction) => void) | null = null

  constructor(options: RuntimeOptions = {}) {
    this.#options = options
    this.clock = options.clock ?? sharedClock()
    this.http = new HttpClient({
      clock: this.clock,
      ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
    })
    this.api = createApi(this.http)
    this.#connectSocket = options.connectSocket ?? true
    this.#rollJitter = options.rollJitter ?? drawRollJitter()
  }

  get ws(): WsClient | null {
    return this.#ws
  }

  /** The channel stores. `null` until `/config` has answered, because
   *  `checksum_levels` comes from it and is not a constant (plan §2.12). */
  get stores(): StoreSet | null {
    return this.#ws?.stores ?? null
  }

  /**
   * Notifies **exactly once**, when `StoreSet` comes into existence.
   *
   * Every channel hook resolves `store ?? EMPTY` for the few hundred
   * milliseconds before `/config` answers, and `EMPTY` never notifies. That is
   * harmless for a component which re-renders for some other reason in that
   * window — and permanent for one that does not: it subscribes `EMPTY` on its
   * first render, never re-renders, and therefore never re-subscribes to the
   * real store. The header's notification bell was exactly that, and it never
   * lit (§F28m).
   *
   * Deliberately **not** `Runtime.state`: subscribing every channel hook to the
   * runtime state would re-render every panel on every roll, every connection
   * change and every clock resync, which is the coupling invariant 2 exists to
   * prevent. This fires once per tab and then never again.
   */
  readonly storesReady = new ExternalStore<boolean>(false)

  get boot(): BootResult | null {
    return this.#booted
  }

  /** The controller, if the wallet chunk has already been loaded. */
  get identity(): IdentityController | null {
    return this.#identity
  }

  /**
   * `GET /config`'s trading block, parsed once.
   *
   * Re-read from `#booted` on every call rather than cached in a field, because
   * `onConfigRevision` re-fetches on a `heartbeat.config_revision` bump and a
   * cached copy would keep the ticket enforcing yesterday's limits. Parsing is
   * a handful of string→bigint conversions and the ticket calls it per render of
   * an order form, not per frame.
   */
  get tradingLimits(): TradingLimits {
    return readTradingLimits(this.#booted?.public.config)
  }

  /**
   * The raw `GET /config` payload, or `null` before it lands.
   *
   * Deliberately untyped at this boundary. Its two consumers read *addresses*
   * out of it — `wallet/chain-targets.ts`'s `targetsFrom` — and both validate the
   * shape themselves, refusing rather than guessing when a field is absent
   * (§F28k). Handing them a typed object would move that validation to the
   * compiler, which cannot see a payload from a venue running a different
   * revision.
   *
   * Read through `#booted` on every call for the same reason `tradingLimits`
   * is: `onConfigRevision` re-fetches on a `heartbeat.config_revision` bump, and
   * a cached copy would keep approving against yesterday's exchange address.
   */
  get config(): unknown {
    return this.#booted?.public.config ?? null
  }

  /**
   * Order entry. Constructed on first use, not at boot.
   *
   * It reaches the signing stack — through `identity()` and through
   * `wallet/order.ts`'s `import()` — so a `Runtime` that built one eagerly would
   * be a `Runtime` that pulled the wallet chunk onto the anonymous cold load by
   * a different door than the one §F21k closed.
   */
  get trading(): OrderController {
    this.#trading ??= new OrderController({
      api: this.api,
      clock: this.clock,
      stores: () => this.stores,
      identity: () => this.ensureIdentity(),
      onAction: (action) => this.#onErrorAction?.(action),
      refreshConfig: async () => {
        const config = await this.api.meta.getExchangeConfig({ noCache: true })
        if (this.#booted) this.#booted.public.config = config as never
      },
    })
    return this.#trading
  }

  /**
   * Reload once if the venue no longer serves this build (`docs/06` §F132,
   * `domain/client-build.ts`). **Once per minimum per tab**: a page served from
   * a stale cache would otherwise reload into the same old build for ever, so a
   * second attempt for the same minimum is reported and left alone.
   */
  #reloadIfTooOld(config: Record<string, unknown>): void {
    const minimum = typeof config.min_client_build === 'string' ? config.min_client_build : null
    if (!buildIsTooOld(APP_VERSION, minimum)) return
    const key = 'ud.reloaded-for-build'
    try {
      if (window.sessionStorage.getItem(key) === minimum) {
        console.warn(`uptik: this page is build ${APP_VERSION} and the venue serves ${minimum} and up; a reload did not fetch it`)
        return
      }
      window.sessionStorage.setItem(key, minimum ?? '')
    } catch {
      // No session storage (a private window, blocked site data): reload once
      // anyway; the guard is lost, not the reload.
    }
    window.location.reload()
  }

  /** Wired by `<WalletProvider>`, which owns the modals a toast action opens. */
  setErrorActionHandler(handler: (action: ErrorAction) => void): void {
    this.#onErrorAction = handler
  }

  /**
   * `config.session_keys.key_ttl_ms` — how long one wallet signature arms
   * trading for, for the copy in the arming modal and the key's provisional
   * local expiry.
   *
   * The venue chooses a key's lifetime since docs/06 §F75, so this is a
   * disclosure rather than a request: the registered key's own `expires_at_ms`
   * is what the client acts on. Read from config rather than written into the
   * sentence, because a hardcoded "7 days" tells the user something false the
   * moment the venue's number moves. The one-day fallback only ever reaches the
   * copy on a `/config` that omits the field.
   */
  get sessionKeyTtlMs(): number {
    const config = (this.#booted?.public.config ?? {}) as Record<string, unknown>
    const sessionKeys = (config.session_keys ?? {}) as { key_ttl_ms?: number }
    return sessionKeys.key_ttl_ms ?? 86_400_000
  }

  /**
   * Load the wallet chunk and build the identity controller, once per tab.
   *
   * Dynamic on purpose. `wallet/controller` reaches `viem/accounts` through
   * `session-key.ts`, and a static import here would make the wallet chunk a
   * dependency of the main bundle — so every anonymous visitor would download
   * the signing stack to render a page that has no wallet in it. Nothing about
   * the page would look wrong, which is exactly why this is a comment and not a
   * convention (`vite.config.ts`, plan §5.1).
   *
   * Called from two places and no others: `#start`, when `/auth/session` says
   * the visitor is signed in, and the Connect button's click handler.
   *
   * **Built from a `/config` that has landed, never from an empty one.** The
   * Connect button renders before the cold load finishes, and a click there
   * used to build the controller — once per tab — from `{}`: every signature
   * then failed "signing configuration is incomplete" until a reload (found on
   * staging's first real run, `docs/06` §F148). Before the boot has its
   * config, this asks for it; the HTTP client shares the boot's request
   * rather than sending a second. A failure is not kept, so the next click
   * tries again.
   */
  ensureIdentity(): Promise<IdentityController> {
    if (this.#identityLoading) return this.#identityLoading
    const loading = Promise.all([
      import('@/wallet/controller'),
      import('@/wallet/eip712'),
      this.#booted ? this.#booted.public.config : this.api.meta.getExchangeConfig({}),
    ]).then(([{ IdentityController }, { toEip712Domain }, landed]) => {
      const config = (this.#booted?.public.config ?? landed ?? {}) as Record<string, unknown>
      const eip712 = (config.eip712 ?? {}) as {
        domain?: Record<string, unknown>
        domain_separator?: string
      }
      // **Validated before it can sign anything, and not by throwing.** The
      // market page renders for anonymous visitors and a venue with a broken
      // signing configuration must cost trading, not the page. The verdict
      // reaches the order ticket through `RuntimeState.signingBlocked`, which
      // `domain/order-validation.ts` turns into a disabled button with a
      // sentence on it; `SessionKeyManager` refuses independently at the two
      // calls that actually produce a signature, because a gate that lives only
      // in the UI is one a later caller reaches around (§F48e).
      const signing = verifySigningDomain(
        toEip712Domain(eip712.domain) as {
          name?: string
          version?: string
          chainId?: number
          verifyingContract?: string
        },
        { targets: targetsFrom(config), pins: CHAIN_PINS },
      )
      if (!signing.signable) {
        this.#patch({ signingBlocked: { code: signing.code, detail: signing.detail } })
        this.state.notify()
      } else if (this.state.state.signingBlocked) {
        this.#patch({ signingBlocked: null })
        this.state.notify()
      }

      const controller = new IdentityController({
        api: this.api,
        clock: this.clock,
        store: this.identityStore,
        ws: () => this.#ws,
        // Every one of these comes from `GET /config` — §1.5 is explicit that
        // clients hardcode no environment-specific address, and the EIP-712
        // domain contains the vault, which rotates.
        //
        // `toEip712Domain` is not a formality: the wire spells the domain
        // `chain_id` / `verifying_contract` and EIP-712 hashes `chainId` /
        // `verifyingContract`, so passing the config object through unrenamed
        // drops both members and signs against a different domain separator
        // (docs/06 §F21l).
        domain: toEip712Domain(eip712.domain) as unknown as Record<string, unknown>,
        // The venue's own separator, checked against the one this client
        // computes from the domain it was handed. Integrity, not trust: it
        // catches a domain mangled on the way here — §F7's family, where the
        // type string reads correctly and the digest is different — and it
        // catches nothing a hostile server would do, since it publishes both.
        ...(typeof eip712.domain_separator === 'string' ?
          { domainSeparator: eip712.domain_separator }
        : {}),
        keyTtlMs: this.sessionKeyTtlMs,
        onAuthenticated: () => {
          void this.#loadPrivateWave()
        },
      })
      this.#identity = controller
      return controller
    })
    loading.catch(() => {
      if (this.#identityLoading === loading) this.#identityLoading = null
    })
    this.#identityLoading = loading
    return loading
  }

  /** Wave B, after an in-session sign-in rather than at cold load (§5.2). */
  async #loadPrivateWave(): Promise<void> {
    const { loadPrivateSnapshot } = await import('./boot')
    const failures: BootFailure[] = []
    const snapshot = await loadPrivateSnapshot(this.api, failures)
    this.#seedPrivate(snapshot)
    this.state.notify()
    if (failures.length) {
      this.#patch({ failures: [...this.state.state.failures, ...failures] })
      this.state.notify()
    }
  }

  /**
   * The private half of the cold load, into the stores.
   *
   * Same argument as `#seedFromBoot`: panels read stores, never fetch results,
   * so the REST snapshot goes in through `StoreSet.apply` on the path the
   * socket's own snapshot takes. Without this the four private channels would
   * render empty until their first frame — and `balance` and `positions` have no
   * snapshot *message* at all (their first `update` is the snapshot), so
   * "empty until the first frame" would mean an empty Positions tab until
   * something moved.
   */
  #seedPrivate(snapshot: PrivateSnapshot | null): void {
    if (!snapshot) return
    const rows = (page: unknown): unknown[] => {
      const data = (page as { data?: unknown[] } | null)?.data
      return Array.isArray(data) ? data : []
    }
    if (snapshot.balance) this.#applySnapshot('balance' as ChannelName, snapshot.balance)
    if (snapshot.positions) {
      this.#applySnapshot('positions' as ChannelName, { positions: rows(snapshot.positions) })
    }
    if (snapshot.openOrders) {
      this.#applySnapshot('orders' as ChannelName, { orders: rows(snapshot.openOrders) })
    }
    if (snapshot.notifications) {
      this.#applySnapshot('notifications' as ChannelName, {
        // Newest-first over HTTP, oldest-first on the socket — the same
        // asymmetry the tape has, handled the same way (`#seedFromBoot`).
        notifications: [...rows(snapshot.notifications)].reverse(),
      })
    }
  }

  /**
   * Follow the session: subscribe the six on sign-in, drop them on logout.
   *
   * Driven off the identity store rather than called from the sign-in path,
   * because there are three ways to arrive at an authenticated session — cold
   * load with a live cookie, SIWE in this tab, and a `SESSION_EXPIRED` recovery
   * — and a subscription that only one of them performs is a Positions tab that
   * is empty depending on how the user got there.
   */
  #followIdentity(): void {
    this.#identityUnsubscribe?.()
    const apply = (initial = false) => {
      const authenticated = this.identityStore.state.phase === 'AUTHENTICATED'
      if (authenticated === this.#privateChannels) return

      /**
       * **Boot's answer wins until the identity store has hydrated**, and this
       * guard is not a nicety.
       *
       * The line above `#followIdentity()`'s call site says it: `hydrate()` is
       * asynchronous, so on a returning visitor's cold load the store still
       * reads `ANONYMOUS` at this instant while the cookie is already known to
       * be good. Without this, the first `apply()` read that, concluded the user
       * had signed out, unsubscribed the six private channels and **cleared
       * every store wave B had just seeded** — balance, positions, orders,
       * settlements and notifications, all fetched and all discarded a
       * millisecond later.
       *
       * It went unnoticed for three phases because five of the six re-seed
       * themselves: the socket sends a snapshot on subscribe, so orders,
       * fills, positions, balance and settlements repopulate within a frame and
       * the screen looks right. `notifications` is the one with no in-band
       * snapshot in the mock (§F28m), so it stayed empty and the bell never lit.
       *
       * Scoped to the **initial** call and to this one direction. A real logout
       * still clears, because that is a transition this subscription observes —
       * and leaving another account's orders on screen is what the clear exists
       * to prevent.
       */
      if (initial && this.#privateChannels && !authenticated) return

      this.#privateChannels = authenticated
      // §9.2.3 step 4 in one line: signing back in retires the banner. The
      // `auth` frame and the six subscriptions are the sign-in path's own work
      // (`IdentityController.signIn`, invariant 16) — this only stops the tab
      // still saying the session expired while it plainly has not.
      if (authenticated && this.state.state.sessionExpired) this.#patch({ sessionExpired: false })
      const { asset, anchorS, periodS } = this.state.state
      this.#ws?.setDesired(this.#desiredChannels(asset, anchorS, periodS))
      // Logging out must not leave another account's orders on screen. The
      // public stores are untouched: the market page is identical signed out.
      if (!authenticated) this.#clearPrivateStores()
    }
    this.#identityUnsubscribe = this.identityStore.subscribe(() => apply())
    apply(true)
  }

  #clearPrivateStores(): void {
    const stores = this.stores
    if (!stores) return
    stores.orders.clear()
    stores.pendingOrders.clear()
    stores.fills.clear()
    stores.positions.clear()
    stores.positionHints.clear()
    stores.balance.clear()
    stores.settlements.clear()
    stores.notifications.clear()
    stores.notifyAll()
  }

  // -------------------------------------------------------------------------

  /**
   * A link to an asset the venue does not run (`/trade/eth` while ETH is off,
   * §F138) boots the default instead: the venue refuses the symbol, and every
   * panel would be a failed request. Only a non-default symbol pays for this
   * read, so `/trade/btc`'s cold load is unchanged.
   */
  async #servedSymbol(symbol: Symbol_): Promise<Symbol_> {
    if (symbol === 'BTC-USD') return symbol
    try {
      const config = await this.api.meta.getExchangeConfig({})
      return servedAssets(config).includes(assetForSymbol(symbol)) ? symbol : 'BTC-USD'
    } catch {
      return symbol // The cold load below reports the failure, as it always has.
    }
  }

  /**
   * The §5.1 cold load, once per tab.
   *
   * Idempotent: React 19 `StrictMode` mounts every effect twice in development,
   * and a second cold load would be a second socket — the one thing D8 forbids
   * outright.
   */
  start(symbol: Symbol_): Promise<void> {
    this.#starting ??= this.#start(symbol)
    return this.#starting
  }

  async #start(requested: Symbol_): Promise<void> {
    const symbol = await this.#servedSymbol(requested)
    const asset = assetForSymbol(symbol)
    this.#patch({ phase: 'BOOTING', asset, symbol, seriesId: seriesIdFor(asset) })
    this.state.notify()

    let result: BootResult
    try {
      result = await boot({
        api: this.api,
        clock: this.clock,
        symbol,
        ...(this.#options.clockSamples === undefined ? {} : { clockSamples: this.#options.clockSamples }),
        ...(this.#options.clockSpreadMs === undefined ? {} : { clockSpreadMs: this.#options.clockSpreadMs }),
      })
    } catch (error) {
      this.#patch({ phase: 'FAILED', error })
      this.state.notify()
      return
    }

    this.#booted = result
    const config = result.public.config as Record<string, unknown>
    // A tab that loaded an index the venue no longer serves — a cached page
    // after a release — reloads before it trades (`docs/06` §F132).
    this.#reloadIfTooOld(config)
    const market = (config.market ?? {}) as { period_seconds?: number }
    const periodS = market.period_seconds ?? DEFAULT_PERIOD_S
    this.#patch({ assets: servedAssets(config) })

    // The socket is created before the stores are seeded because `StoreSet`
    // lives on it and carries `checksum_levels` from this very response.
    const wsConfig = resolveWsConfig((config.ws ?? {}) as never)
    this.#ws = new WsClient({
      config: wsConfig,
      clock: this.clock,
      snapshotSource: (descriptor) => this.#snapshotFor(descriptor),
      boundaryGuard: () => this.#boundaryHoldMs(),
      onStatus: (status) => this.#onStatus(status),
      onSessionExpired: () => this.#onSessionExpired(),
      onProblem: (problem) => this.#onProblem(problem),
      onHeartbeat: (data) => this.#onHeartbeat(data),
      onVisible: (hiddenMs) => this.#onVisible(hiddenMs),
      onHidden: () => this.#onHidden(),
      onConfigRevision: () => {
        // `heartbeat.config_revision` moved. Re-fetch with `no-cache`; the ETag
        // keeps the revalidation cheap (openapi `x-ud-cache` on `/config`).
        //
        // **And keep what came back.** Until `docs/06` §F132 the answer was
        // thrown away, so a revision change re-fetched and changed nothing —
        // and the revision itself never changed, so nobody noticed. It moves
        // with the content now, which is how a release reaches an open tab.
        void this.api.meta
          .getExchangeConfig({ noCache: true })
          .then((fresh) => {
            if (this.#booted) this.#booted.public.config = fresh as never
            this.#reloadIfTooOld(fresh as Record<string, unknown>)
          })
          .catch(() => {
            // The next heartbeat asks again; nothing here is worth a banner.
          })
      },
    })

    // The stores exist from this line on. One notification, once per tab —
    // every channel hook is waiting for it and nothing else fires it.
    // `set` only marks dirty — the rAF flush is what publishes, and this store
    // is not in that flush. `notify()` is the whole point of the store.
    this.storesReady.set(true)
    this.storesReady.notify()

    const anchorS = anchorAt(this.clock.now(), periodS)
    this.#patch({
      phase: 'READY',
      periodS,
      anchorS,
      marketId: buildMarketId(seriesIdFor(asset), anchorS),
      clock: this.clock.status,
      failures: result.failures,
      bootedAtMs: Math.floor(this.clock.now()),
    })
    // See the field's own comment: the cold load *is* a reconciliation, so the
    // first real one is bounded to the session rather than to all history.
    this.#reconciledToMs = this.state.state.bootedAtMs

    this.#seedFromBoot(result, anchorS, periodS)
    this.#seedPrivate(result.private)
    this.state.notify()

    // Seeded from `/auth/session` rather than from the identity store, and the
    // difference matters on a cold load: `hydrate()` is asynchronous, so the
    // store still says `ANONYMOUS` at this instant while the cookie is already
    // known to be good. Taking the store's word here would cost a returning
    // visitor a second subscribe frame — §5.2 puts the six in wave B of the cold
    // load, not a tick after it.
    this.#privateChannels = result.authenticated
    this.#followIdentity()

    // A returning visitor is already signed in, so the wallet chunk is not
    // speculative for them — it is needed to reconcile the local key, which is
    // the phase's second exit criterion. An anonymous visitor never reaches
    // this branch and never downloads it (§5.1).
    if (result.authenticated) {
      void this.ensureIdentity()
        .then((identity) => identity.hydrate(result.session as never))
        .catch((error: unknown) => {
          // Identity is not on the critical path for the market page. A failure
          // here leaves the header showing Connect, which is wrong but harmless
          // and recoverable by clicking it.
          this.#patch({ failures: [...this.state.state.failures, { step: 'identity', error }] })
          this.state.notify()
        })
    }

    // Everything below is after first paint by construction: the stores are
    // seeded and notified above, and none of it is awaited.
    // The holds before the first `setDesired`, so a cold load that lands inside
    // a resolution window subscribes the predecessor in the *same* frame as
    // everything else rather than in a second one a tick later. `#applyHolds`
    // is a pure function of `(asset, anchor, now)`, so a tab booting at `T+65`
    // arrives at exactly the subscriptions a tab that has been open all minute
    // holds — including the resolving predecessor the secondary surface renders.
    this.#applyHolds()
    if (this.#connectSocket) {
      this.#ws.connect()
      this.#ws.setDesired(this.#desiredChannels(asset, anchorS, periodS))
    }

    this.#keepWarm = new KeepWarm({ client: this.http })
    this.#keepWarm.start()
    this.#watchNetwork()

    // The 15-minute backstop. The continuous correction is the 5-second pong
    // (§F11e); this covers cold load, wake and reconnect.
    this.#clockScheduler = new ClockSyncScheduler({
      clock: this.clock,
      fetchTime: timeFetcherFor(this.api),
    })
    this.#clockScheduler.start()

    result.clockSync
      .then(() => {
        this.#patch({ clock: this.clock.status })
        this.state.notify()
      })
      .catch(() => {
        this.#patch({ clock: this.clock.status })
        this.state.notify()
      })

    this.#startRoll()
  }

  stop(): void {
    this.#identityUnsubscribe?.()
    this.#identityUnsubscribe = null
    this.#networkUnsubscribe?.()
    this.#networkUnsubscribe = null
    this.#clearTimer(this.#depthTimer)
    this.#depthTimer = null
    this.#ws?.close()
    this.#keepWarm?.stop()
    this.#clockScheduler?.stop()
    this.#roll?.stop()
    this.#roll = null
    this.#starting = null
  }

  // -------------------------------------------------------------------------
  // Seeding
  // -------------------------------------------------------------------------

  /**
   * Apply the cold-load response into the channel stores.
   *
   * Through `StoreSet.apply` with a synthesised `snapshot` frame, which is the
   * same path `WsClient` takes when it repairs a gap from REST — a second apply
   * path would be a second place for the ladder to be assembled slightly
   * differently.
   *
   * Two shapes need adjusting on the way in, and both are real differences
   * between the transports rather than bugs:
   *
   * - **The tape arrives newest-first over HTTP and oldest-first on the
   *   socket.** `StoreSet` reverses what it is given, correctly, for the socket
   *   shape; handing it the REST array unreversed would put the oldest print at
   *   the top of the panel.
   * - **The series rail exists on one transport only.** `series.{id}` carries
   *   `markets[]`; `GET /series` does not, because over HTTP the rail is its own
   *   endpoint (§F18c's exemption). The two REST responses are combined here
   *   into the socket's shape so `<SeriesRail>` has one thing to read.
   */
  #seedFromBoot(result: BootResult, anchorS: number, periodS: number): void {
    const stores = this.stores
    if (!stores) return
    const asset = assetForSymbol(result.symbol)
    const seriesId = seriesIdFor(asset)
    const marketId = buildMarketId(seriesId, anchorS)

    const entry = firstStripEntry(result.public.currentMarkets, result.symbol)
    if (entry) {
      for (const market of [entry.previous, entry.current, entry.next]) {
        const id = idOf(market)
        if (id) this.#applySnapshot(`market.${id}` as ChannelName, market)
      }
      // The three `include=` payloads §F18d gave the response schema a place to
      // put. This is what makes first meaningful paint one request.
      if (entry.orderbook) this.#applySnapshot(`book.${marketId}` as ChannelName, entry.orderbook)
      if (entry.trades) {
        this.#applySnapshot(`trades.${marketId}` as ChannelName, {
          trades: [...entry.trades].reverse(),
        })
      }
      if (entry.candles) {
        this.#applySnapshot(`candles.${marketId}.1s` as ChannelName, { candles: entry.candles })
      }
    }

    if (result.public.index) {
      this.#applySnapshot(`index.${result.symbol}` as ChannelName, result.public.index)
    }

    const rail = railSnapshotFrom(result, asset, anchorS, periodS)
    if (rail) this.#applySnapshot(`series.${seriesId}` as ChannelName, rail)
  }

  #applySnapshot(channel: ChannelName, data: unknown): void {
    this.stores?.apply(channel, [
      { type: 'snapshot', channel, server_time_ms: Math.floor(this.clock.now()), data } as ChannelFrame,
    ])
  }

  /**
   * The REST counterpart of a channel, for gap recovery (docs/03 Appendix A).
   *
   * Best-effort and never decisive: it repaints immediately, and the panel stays
   * marked stale until the socket's own snapshot lands.
   */
  async #snapshotFor(descriptor: { kind: string; marketId?: string; symbol?: string; asset?: string }): Promise<unknown> {
    try {
      switch (descriptor.kind) {
        case 'book':
          return descriptor.marketId ? await this.api.markets.getOrderBook(descriptor.marketId) : null
        case 'trades': {
          if (!descriptor.marketId) return null
          const page = await this.api.markets.getMarketTrades(descriptor.marketId)
          return { trades: [...((page as { data?: unknown[] }).data ?? [])].reverse() }
        }
        case 'candles': {
          if (!descriptor.marketId) return null
          const page = await this.api.markets.getMarketCandles(descriptor.marketId, { interval: '1s' })
          return { candles: (page as { data?: unknown[] }).data ?? [] }
        }
        case 'market':
          return descriptor.marketId ? await this.api.markets.getMarket(descriptor.marketId) : null
        case 'index':
          return descriptor.symbol ? await this.api.index.getIndexPrice(descriptor.symbol as Symbol_) : null
        case 'chat': {
          if (!descriptor.asset) return null
          // REST is newest first; the channel's snapshot is oldest first.
          const page = await this.api.chat.listChatMessages(descriptor.asset as Asset)
          return { messages: [...page.messages].reverse() }
        }
        default:
          return null
      }
    } catch {
      return null
    }
  }

  // -------------------------------------------------------------------------
  // Subscriptions
  // -------------------------------------------------------------------------

  /**
   * The cold-load channel set of §3.1, plus the roll's holds and anything a
   * switch retained.
   *
   * `market.{other N}` and both `index.*` are here on cold load **on purpose**:
   * the asset switch must not blank the countdown or the strip price, and the
   * BTC/ETH segmented control shows both live prices without switching.
   *
   * **`market.{other N+1}` is the thirteenth channel, and §5.1's "twelve" is now
   * wrong** (register §F25). Both assets' *current* markets were in the base set
   * and only the focused asset's *successor* was, so at every boundary the
   * sibling's successor became new and the sibling's successor-of-the-successor
   * became stale — one subscribe and one unsubscribe, at the one instant §6.0
   * Fact 2 forbids any control-plane traffic at all. Subscribing the sibling's
   * successor alongside the focus's makes the set at `T+60−ε` and `T+60+ε`
   * **identical**, so `setDesired` diffs to nothing and the swap sends no frame.
   * That is what "no work at the boundary beyond re-targeting subscriptions
   * already held" costs, and it costs one channel.
   */
  /**
   * What this tab wants subscribed at this instant.
   *
   * Public because §9.2.4's rule is about the *set* — four depth channels go
   * and nine stay — and with no socket there is no `subscribe` frame to read it
   * off. `#applyHolds` recomputes and publishes; this answers.
   */
  desired(): ChannelName[] {
    const { asset, anchorS, periodS } = this.state.state
    return this.#desiredChannels(asset, anchorS, periodS)
  }

  #desiredChannels(asset: Asset, anchorS: number, periodS: number): ChannelName[] {
    return desiredChannels({
      asset,
      assets: this.state.state.assets,
      anchorS,
      periodS,
      holds: this.#holds,
      depthSuspended: this.#depthSuspended,
      ...(this.#retainedTicker ? { retainedTicker: this.#retainedTicker } : {}),
      privateChannels: this.#privateChannels,
    })
  }

  // -------------------------------------------------------------------------
  // Resilience (Phase 9 — docs/03 §9.2)
  // -------------------------------------------------------------------------

  /**
   * §9.2.1 and §9.2.5, in one place: what changes when the socket does.
   *
   * The reconcile fires on the **transition back to `OPEN` after a loss**, never
   * on the first open of a tab's life — the cold load already fetched all of
   * this, and firing here would double every request on every page view.
   */
  #onStatus(status: ConnectionStatus): void {
    const previous = this.state.state.connection
    if (status === previous) return
    const patch: Partial<RuntimeState> = {
      connection: status,
      connectionSinceMs: connectionClockAfter(previous, status, this.state.state.connectionSinceMs, monoNow()),
    }
    if (status === 'OPEN') {
      // A new socket is a new stream: whatever terminal error took the last one
      // is no longer the thing on screen, and leaving it there would leave a
      // `SLOW_CONSUMER` banner up over a healthy connection.
      patch.lastProblem = null
      if (this.#everOpen) patch.resumed = true
      this.#everOpen = true
    }
    this.#patch(patch)
    this.state.notify()
    if (status === 'OPEN' && previous !== 'OPEN' && this.state.state.resumed) {
      void this.reconcile()
    }
  }

  /**
   * §9.2.3. The session behind the socket expired; the socket did not.
   *
   * **Anonymous locally and immediately, before the confirming fetch.** The
   * balance chip, the positions tab and the ticket are all rendering an account
   * the server has already stopped serving, and a round trip is ~35 ms of a
   * screen telling the user they have money they can spend. `#followIdentity`
   * observes the transition and drops the six private channels in one frame.
   *
   * `applySession(null)` and **not** `applyLogout()`, and the difference is rule
   * 5 of §9.2.3: the session key is bound to the wallet, not to the session
   * (D3). A user whose cookie expired still holds a valid key and must be able
   * to sign back in with one signature and no second popup — `applyLogout()`
   * would forget the key and cost them the extra one.
   *
   * The public half is untouched throughout. An expired session must never blank
   * the book.
   */
  #onSessionExpired(): void {
    this.identityStore.applySession(null)
    this.#patch({ sessionExpired: true })
    this.state.notify()
    void this.#confirmSession()
  }

  /** Step 3: confirm with the server rather than trusting one frame, then clear
   *  the cookie deterministically so the tab is in one known state. */
  async #confirmSession(): Promise<void> {
    let session: unknown = null
    try {
      session = await this.api.auth.getSession()
    } catch {
      // A 401 here *is* the answer. `HttpClient` re-reads `/auth/session` once
      // on any 401 by itself, so this has already been asked twice.
      session = null
    }
    if ((session as { authenticated?: boolean } | null)?.authenticated) {
      // The frame described a session that had already been replaced — a
      // re-auth racing the expiry. Put the user back rather than signing them
      // out of a session that works.
      this.identityStore.applySession(session as never)
      this.#patch({ sessionExpired: false })
      this.state.notify()
      return
    }
    try {
      await this.api.auth.logout()
    } catch {
      /* clearing local state is the point; the server's opinion is already known */
    }
  }

  /**
   * The banner's one non-status input.
   *
   * Only terminal codes are held: a `RATE_LIMITED` is a thing that happened, not
   * a state the connection is in, and pinning it to the banner would leave a
   * warning up over a socket that recovered a second later. `#onStatus` clears
   * this on the next clean `OPEN`.
   */
  #onProblem(problem: { code?: string; terminal?: boolean }): void {
    if (problem.terminal !== true) return
    if (problem.code === 'SESSION_EXPIRED') return
    this.#patch({ lastProblem: (problem.code ?? null) as WsErrorCode | null })
    this.state.notify()
  }

  /** §9.2.12. `venue_status` drives the banner; `mode` reaches the ticket
   *  through `GET /config`, which the same beat's `config_revision` re-fetches. */
  #onHeartbeat(data: HeartbeatData): void {
    const patch: Partial<RuntimeState> = {}
    if (data.venue_status !== undefined && data.venue_status !== this.state.state.venueStatus) {
      patch.venueStatus = data.venue_status
    }
    if (data.connection_id && data.connection_id !== this.state.state.connectionId) {
      patch.connectionId = data.connection_id
    }
    if (Object.keys(patch).length === 0) return
    this.#patch(patch)
    this.state.notify()
  }

  /**
   * The two visibility handlers, reachable from a test.
   *
   * `WsClient` owns the `visibilitychange` listener (`heartbeat.ts` needs it for
   * the ping cadence) and calls these two through its handlers. A headless test
   * has no `WsClient` — `connectSocket: false` is how it avoids opening a socket
   * — so without this the 90-second depth drop and the whole wake checklist
   * would be reachable only from a browser, which is exactly the shape of thing
   * `docs/13` says gets ticked without being verified.
   */
  handleVisibility(visible: boolean, hiddenMs = 0): void {
    if (visible) this.#onVisible(hiddenMs)
    else this.#onHidden()
  }

  /**
   * §9.2.4. The tab came back.
   *
   * The checklist in order, and step 1 is the one that is easy to skip: the
   * screen is already rendering from local state and marked stale, because
   * nothing blanked it while it was hidden.
   *
   * Depth comes back **before** the reconcile rather than after, because the
   * ladder is the panel a returning user looks at first and the subscription's
   * in-band snapshot is faster than the HTTP wave.
   */
  #onVisible(hiddenMs: number): void {
    this.#hidden = false
    const marketIdWhenHidden = this.#hiddenAtMarketId
    this.#hiddenAtMarketId = null
    if (this.#depthSuspended) {
      this.#depthSuspended = false
      // Recomputed from the clock, never remembered from before the tab was
      // hidden: a tab hidden across three boundaries wants the *current*
      // market's ladder and has no use for the one it was watching (invariant
      // 18 — the set is a pure function of `(asset, anchor, now)`).
      this.#applyHolds()
    }
    // A suspended laptop's wall clock is the classic silent killer here, and the
    // countdown is the one number on screen that must not be confidently wrong.
    if (hiddenMs > WAKE_RESYNC_THRESHOLD_MS) {
      void this.#clockScheduler
        ?.resync('WAKE')
        .catch(() => {})
        .finally(() => {
          this.#patch({ clock: this.clock.status })
          this.state.notify()
        })
    }
    void this.#onWake(marketIdWhenHidden)
  }

  /**
   * docs/03 §9.2.4 steps 6 and 7, and the difference between them is the row.
   *
   * §9.2.4 is explicit and has two clauses: *unconditionally* fetch balance,
   * positions and open orders — **three** requests — and run §9.2.5's whole
   * wave only *if the market id changed while hidden*. This called
   * `reconcile()` on every `visibilitychange → visible`, so an alt-tab of any
   * duration cost six requests plus whatever `#resnapshotDepth` added, on a tab
   * whose socket had never stopped delivering. At the cadence a user actually
   * switches tabs that is the largest ungated HTTP source in the client
   * (`docs/17`).
   *
   * **The gate is the market id and not `hiddenMs`**, because a duration is a
   * proxy and the id is the fact. A tab hidden for two seconds across a
   * boundary has missed a roll; one hidden for fifty seconds inside a single
   * market has not, however long that felt. `#onHidden` records the id and this
   * compares it, which is `docs/03`'s own clause executed rather than
   * approximated.
   *
   * The three that stay unconditional stay because the specification says
   * unconditionally. See `docs/17`'s annotation for why that is a residual
   * rather than a decision taken here.
   */
  async #onWake(marketIdWhenHidden: string | null): Promise<void> {
    if (this.state.state.phase !== 'READY') return
    const rolled = marketIdWhenHidden !== null && marketIdWhenHidden !== this.state.state.marketId
    if (rolled) return this.reconcile()
    if (!this.#privateChannels) return
    await Promise.all([this.#refreshBalance(), this.#refreshPositions(), this.#refreshOpenOrders()])
    this.stores?.notifyAll()
    this.state.notify()
  }

  #onHidden(): void {
    this.#hidden = true
    // §9.2.4 step 7's condition, taken at the only moment it can be: the id on
    // screen now is what "changed while hidden" is measured against.
    this.#hiddenAtMarketId = this.state.state.marketId
    this.#clearTimer(this.#depthTimer)
    // A hidden tab receiving 124 frames/s in the last five seconds of every
    // minute is a battery bug. `market.*`, `series.*`, `index.*`, `heartbeat`
    // and every private channel are kept, so notifications and settlements
    // still land and the tab title can still count down.
    this.#depthTimer = this.#setTimer(() => {
      if (!this.#hidden) return
      this.#depthSuspended = true
      this.#applyHolds()
    }, HIDDEN_DEPTH_DROP_MS)
  }

  /**
   * §9.2.5. One wave, bounded, after a gap that may have spanned whole markets.
   *
   * > At a 60-second cadence, a 30-second disconnect spans half a market and a
   * > 75-second one spans an entire market the client never saw open.
   *
   * Public first and always — `GET /markets/current` is one request for the
   * strip, the book, the tape and the chart, and it is also how the client
   * learns the roll happened without it. Private only when there is a session,
   * and **bounded**: without `since_ms` a client returning after eight hours
   * pulls 480 markets of settlements.
   *
   * The reconciliation **replaces** local state rather than merging into it.
   * Never show a position that no longer exists.
   *
   * Public so the wake path, the reconnect path and a test can all call the same
   * one. There is no second implementation for "long" gaps: `missed_rolls` of 1
   * and of 480 take this path with different bounds.
   */
  async reconcile(): Promise<void> {
    if (this.state.state.phase !== 'READY') return
    if (this.#reconciling) return this.#reconciling
    const run = this.#reconcile().finally(() => {
      this.#reconciling = null
    })
    this.#reconciling = run
    return run
  }

  /**
   * §9.2.4 step 6's three, as three methods with one caller each way.
   *
   * They exist as methods rather than inline in `#reconcile` because §9.2.4 and
   * §9.2.5 both fetch them and a second copy of "replace the positions" is how
   * two paths come to disagree about whether replacing means `clear()` first.
   * Each swallows its own failure: a reconciliation that fails half-way leaves
   * the panel showing what it had, which is stale and correct, rather than
   * blank.
   */
  #refreshBalance(): Promise<void> {
    return this.api.portfolio
      .getBalance()
      .then((balance) => this.#applySnapshot('balance' as ChannelName, balance))
      .catch(() => {})
  }

  #refreshPositions(): Promise<void> {
    return this.api.portfolio
      .listPositions()
      .then((page) => {
        // **Replaced, not merged**, and only once the response is in hand —
        // clearing before the fetch would blank the panel if it failed.
        //
        // §9.2.5: *never show a position that no longer exists.* A position
        // opened, expired, resolved and settled entirely inside the gap is
        // gone from the venue's answer, and `upsert` alone would leave the
        // row on screen forever with nothing left to update it.
        this.stores?.positions.clear()
        this.#applySnapshot('positions' as ChannelName, { positions: rowsOf(page) })
      })
      .catch(() => {})
  }

  #refreshOpenOrders(): Promise<void> {
    return this.api.trading
      .listOrders({ status: ['OPEN'] })
      // An open order in a market that no longer exists was cancelled at
      // expiry (`MARKET_EXPIRED`). Reconciled silently: the user did not do
      // it and there is nothing for them to decide — which is only true if
      // the row actually goes, so this replaces too.
      .then((page) => {
        this.stores?.orders.clear()
        this.#applySnapshot('orders' as ChannelName, { orders: rowsOf(page) })
      })
      .catch(() => {})
  }

  async #reconcile(): Promise<void> {
    const stores = this.stores
    if (!stores) return
    const { symbol, asset, periodS } = this.state.state
    const sinceMs = this.#reconciledToMs

    const publicWave = this.api.markets
      // **Three parameters, not one comma-joined value.** `include` is
      // `style: form, explode: true` in openapi, which is `include=orderbook&
      // include=trades&include=candles` on the wire, and every server that
      // reads it reads it with the equivalent of `getAll`. Sent joined it
      // matched nothing, so the response carried no book, no tape and no chart
      // — and the reconcile the whole of §9.2.5 is built around repaired the
      // market strip and nothing else. `boot.ts` and `switchAsset` had it right;
      // this call had an `as never` on it, which is how a shape the generated
      // types would have rejected got through.
      //
      // A silently ignored parameter is not a wrong answer, it is *no* answer,
      // and every check in this repository is a check on an answer (§F39.10).
      .getCurrentMarkets({ symbol: [symbol], include: [...STRIP_INCLUDE] })
      .then(async (response) => {
        // The anchor the venue says is current, not the one this tab was
        // holding. A tab that was away for four minutes is pointed at a market
        // that expired three markets ago.
        const anchorS = anchorAt(this.clock.now(), periodS)
        if (anchorS !== this.state.state.anchorS) this.#retarget(anchorS)
        const carried = this.#reconcileStrip(response, symbol, this.state.state.anchorS, asset)
        await this.#resnapshotDepth(carried)
      })
      .catch((error: unknown) => {
        this.#patch({ failures: [...this.state.state.failures, { step: 'reconcile', error }] })
      })

    if (!this.#privateChannels) {
      await publicWave
      stores.notifyAll()
      this.state.notify()
      return
    }

    const settlements = this.api.portfolio
      .listSettlements({ ...(sinceMs === null ? {} : { since_ms: sinceMs }), limit: 25 })
      .catch(() => null)

    const [, settled] = await Promise.all([
      publicWave,
      settlements,
      this.#refreshBalance(),
      this.#refreshPositions(),
      this.#refreshOpenOrders(),
      this.api.trading
        .listFills({ ...(sinceMs === null ? {} : { start_ms: sinceMs }), limit: 100 })
        .then((page) => this.#applySnapshot('fills' as ChannelName, { fills: rowsOf(page).reverse() }))
        .catch(() => {}),
    ])

    const rows = rowsOf(settled)
    if (rows.length > 0) {
      this.#applySnapshot('settlements' as ChannelName, { settlements: rows })
      // **The tab summarises the gap, not the response.** Every row goes into
      // the Settled tab, because the tab is the durable record and a settlement
      // the venue reports is one that happened. Only the rows that settled
      // *inside this tab's own gap* are counted and toasted — §9.2.5's rule is
      // "never animate a payout the user did not watch", and a payout from last
      // night is the clearest case of one.
      //
      // The bound is applied here as well as sent as `since_ms` because the two
      // answer different questions: the parameter is what keeps the response
      // small, and this is what keeps the sentence true. A server that ignores
      // the filter — every mock in this repository does — would otherwise put
      // "While you were away — 25 markets settled" on a tab five seconds old
      // (`docs/17`).
      const away = sinceMs === null ? rows : rows.filter((row) => settledAfter(row, sinceMs))
      // Counted, not replayed. `<SettlementReceiptStack>` already refuses to
      // toast anything older than `TOAST_MAX_AGE_MS`, so the animation is not
      // the risk here — the summary is what a returning user needs instead of
      // six receipts they cannot act on.
      if (away.length > 0) {
        this.#patch({ awayMarkets: away.length })
        this.#summariseAway(away)
      }
    }
    // **Floored.** `Clock.now()` is `performance.now() + offset` and carries
    // sub-millisecond precision; every timestamp on the wire is an integer epoch
    // millisecond, and `since_ms=1786637434187.336` is a bound no server has to
    // accept. This is the third time this exact fix has been needed — the other
    // two are `approvalNonce` (§F21) and `trading/ids.ts` — and all three came
    // from the same place: the corrected clock is a float and a `_ms` field is
    // not.
    this.#reconciledToMs = Math.floor(this.clock.now())
    stores.notifyAll()
    this.state.notify()
  }

  /**
   * §9.2.5 step 4, as one line.
   *
   * > "While you were away - 2 markets settled, +$14.20" → links to Settled
   *
   * The money is summed in `bigint` micro-USDC and rendered once (invariant 6):
   * a float over six 6-decimal strings is how a summary comes to disagree with
   * the rows it summarises. `net_pnl` is the settlement's own signed figure, so
   * nothing here re-derives a payout.
   */
  #summariseAway(rows: readonly unknown[]): void {
    let net = 0n
    for (const row of rows) {
      const value = (row as { net_pnl?: unknown }).net_pnl
      if (typeof value !== 'string') continue
      try {
        net += parseFixed(value, USDC_DP)
      } catch {
        // A malformed decimal is not worth losing the count over — the row is
        // in the Settled tab either way, and that is the durable record.
      }
    }
    pushToast({
      tone: 'info',
      title: 'While you were away',
      body: `${rows.length} ${rows.length === 1 ? 'market' : 'markets'} settled, ${formatSignedUsdc(
        formatFixed(net, USDC_DP),
      )}`,
      action: { label: 'Settled', run: () => requestBottomTab('settled') },
    })
  }

  /**
   * §9.2.8. Ask the clock to prove itself again.
   *
   * Called by `<ClockPill>` when the offset crosses tolerance or a handshake
   * fails its sample floor. `ClockSyncScheduler.resync` shares an in-flight run
   * and restarts the 15-minute interval, so this is not a second timer and does
   * not become one however often it is called — invariant 8.
   */
  async resyncClock(): Promise<void> {
    try {
      await this.#clockScheduler?.resync('MANUAL')
    } catch {
      // A failed re-sync leaves the pill up, which is the correct outcome: the
      // clock is still unverified and the user is still being told so.
    }
    this.#patch({ clock: this.clock.status })
    this.state.notify()
  }

  /** The summary line has been shown. It describes one gap and must not survive
   *  into the next one. */
  clearAwaySummary(): void {
    if (this.state.state.awayMarkets === 0) return
    this.#patch({ awayMarkets: 0 })
    this.state.notify()
  }

  /**
   * §9.2.6. Network changes, and the one thing the browser tells us for free.
   *
   * `online`/`offline` are hints for copy, never gates on a request: a captive
   * portal reports online, and a browser that has decided it is offline can
   * still be wrong. The load-bearing half is the **forced reconnect on
   * `online`** — a mobile transition through a dead zone can hold a TCP
   * connection open for 60+ seconds, which on this product is an entire market
   * the user watches through a book that stopped moving.
   *
   * **Not on `navigator.connection`'s `change`** (`docs/06` §F119,
   * 2026-10-10). Chrome fires it whenever its *estimate* of the link moves — a
   * round trip going from 50 to 75 ms — not only when the network does, and on
   * one trader's Linux Chrome that was every few seconds: 1,259 reconnects in a
   * day, each one greying the ticket and re-fetching the account. A connection
   * that really died without an `offline` is the heartbeat's to find, two
   * missed pongs (10–15 s), which is still far short of the TCP timeout.
   */
  #watchNetwork(): void {
    const target = globalThis as unknown as {
      addEventListener?: (type: string, fn: () => void) => void
      removeEventListener?: (type: string, fn: () => void) => void
      navigator?: { onLine?: boolean }
    }
    if (!target.addEventListener) return
    const setOnline = (online: boolean) => {
      if (this.state.state.online === online) return
      this.#patch({ online })
      this.state.notify()
    }
    const onOffline = () => setOnline(false)
    const onOnline = () => {
      setOnline(true)
      this.#forceReconnect()
    }

    setOnline(target.navigator?.onLine ?? true)
    target.addEventListener('offline', onOffline)
    target.addEventListener('online', onOnline)
    this.#networkUnsubscribe = () => {
      target.removeEventListener?.('offline', onOffline)
      target.removeEventListener?.('online', onOnline)
    }
  }

  /** Don't wait out a TCP timeout the client already knows is pointless. The
   *  close handler schedules the backoff, so there is still one reconnect path. */
  #forceReconnect(): void {
    const ws = this.#ws
    if (!ws) return
    if (ws.status === 'OPEN' || ws.status === 'CONNECTING') ws.close()
    ws.connect()
  }

  #setTimer(fn: () => void, ms: number): number {
    return globalThis.setTimeout(fn, ms) as unknown as number
  }

  #clearTimer(handle: number | null): void {
    if (handle !== null) globalThis.clearTimeout(handle)
  }

  // -------------------------------------------------------------------------
  // The roll (Phase 7)
  // -------------------------------------------------------------------------

  /** `config.market`'s own offsets. `δ` is not 13,000 — invariant 11. */
  #rollTiming(): RollTiming {
    const config = (this.#booted?.public.config ?? {}) as Record<string, unknown>
    const market = (config.market ?? {}) as { resolve_eta_offset_ms?: number }
    return {
      periodMs: this.state.state.periodS * 1000,
      // §F26: the settlement window closes at expiry, so the fallback is
      // publication latency alone. It is a fallback, not the value — `/config`
      // is the source, and the whole point of §F24 and §F26 landing cheaply is
      // that this number appears here once and nowhere else.
      resolveEtaOffsetMs: market.resolve_eta_offset_ms ?? 2_000,
    }
  }

  /**
   * Recompute which markets this tab holds beyond the focused one, and publish
   * the result as one batched control frame.
   *
   * **Declarative, not accumulated**, and that is the whole reason the roll's
   * five instants are a *schedule* rather than a state machine. Each hold is a
   * comparison between the corrected clock and one of `domain/roll.ts`'s
   * instants, so the set is a pure function of `(asset, anchor, now)` — which
   * means a cold load at `T+45`, an asset switch at `T+50` and the pre-warm
   * firing on time all produce the same subscriptions, and none of them needs a
   * catch-up path. The scheduler's only job is to wake at the moments where the
   * answer changes.
   *
   * `atMs` is the scheduled instant of the event that called, never
   * `clock.now()`: see `RollSchedulerOptions.onEvent`.
   */
  /**
   * How long a control frame must wait for the market boundary to pass.
   *
   * §6.0 Fact 2 and invariant 18: all 3,000 tabs reach `T+60` inside one clock
   * error of each other, so **nothing may be fetched, subscribed or negotiated
   * there**. The roll itself obeys that by construction — the desired set at
   * `T+60−ε` equals the set at `T+60+ε`, so the swap sends nothing — but a
   * `seq` gap has no such guarantee: it arrives when the gateway drops a frame,
   * which is likeliest in the last five seconds of a market, and the recovery
   * then unsubscribed and resubscribed straight across the boundary
   * (`docs/17`).
   *
   * Asymmetric on purpose. A recovery **inside** the window waits for the far
   * edge; one that arrives after it goes immediately, because by then the herd
   * has passed and holding would only delay a repair. The far edge carries the
   * tab's own pre-warm jitter so the releases are spread too — a guard that
   * released every waiting tab at `T+60+ε` would move the herd rather than
   * remove it.
   */
  #boundaryHoldMs(): number {
    const { phase, anchorS, periodS } = this.state.state
    if (phase !== 'READY') return 0
    const untilBoundaryMs = (anchorS + periodS) * 1000 - this.clock.now()
    if (untilBoundaryMs > BOUNDARY_QUIET_MS || untilBoundaryMs <= -BOUNDARY_QUIET_MS) return 0
    return Math.ceil(untilBoundaryMs + BOUNDARY_QUIET_MS + this.#rollJitter.prewarmMs / 8)
  }

  #applyHolds(atMs: number = this.clock.now()): void {
    const { asset, anchorS, periodS, phase } = this.state.state
    if (phase !== 'READY') return
    const nowMs = Math.max(this.clock.now(), atMs)

    this.#holds = rollHolds(anchorS, periodS, nowMs, this.#rollTiming(), this.#rollJitter)
    const previousS = anchorS - periodS
    // The predecessor, resolving behind the market on screen — the secondary
    // surface's subject, and it is held exactly as long as `market.{id}` is.
    const resolving = this.#holds.markets.includes(previousS)
    this.#patch({
      resolvingMarketId: resolving ? buildMarketId(seriesIdFor(asset), previousS) : null,
    })
    const channels = this.#desiredChannels(asset, anchorS, periodS)
    this.#ws?.setDesired(channels)
    // The stores follow the subscriptions, which is the same rule one layer
    // down: `RollScheduler` fires `TEARDOWN` and `RETIRE` for the focused
    // asset's predecessor only, so the sibling asset's `market.{id}` store was
    // created every minute and dropped never, and an asset switch stranded a
    // whole set of depth stores (`StoreSet.retainSubscribed`, `docs/17`).
    //
    // **Not while the depth is suspended.** A hidden tab has no depth channels
    // in its desired set and must keep its ladder: §9.2.4 rule 1 is *never
    // blank*, and dropping the stores here would make the wake path empty
    // rather than stale.
    if (!this.#depthSuspended) this.stores?.retainSubscribed(channels)
    this.state.notify()
  }

  #startRoll(): void {
    this.#roll?.stop()
    this.#roll = new RollScheduler({
      now: () => this.clock.now(),
      getCycle: () => {
        const { phase, anchorS } = this.state.state
        if (phase !== 'READY') return null
        return { anchorMs: anchorS * 1000, timing: this.#rollTiming() }
      },
      // Off the payload, never `expiry + 13_000`: a server that slips
      // `resolve_eta_ms` slips it on exactly the market the watchdog exists for.
      resolveEtaFor: (marketId) => {
        const market = this.stores?.marketFor(marketId).value as Record<string, unknown> | null
        const eta = market?.resolve_eta_ms
        return typeof eta === 'number' ? eta : null
      },
      predecessorId: () => {
        const { asset, anchorS, periodS } = this.state.state
        return anchorS - periodS > 0 ? buildMarketId(seriesIdFor(asset), anchorS - periodS) : null
      },
      successorId: () => {
        const { asset, anchorS, periodS } = this.state.state
        return buildMarketId(seriesIdFor(asset), anchorS + periodS)
      },
      jitter: this.#rollJitter,
      onEvent: (kind, marketId, atMs) => this.#onRollEvent(kind, marketId, atMs),
    })
    this.#roll.start()
  }

  /**
   * The five instants, and how little each of them does.
   *
   * `PREWARM`, `TEARDOWN` and `RETIRE` are all the same call, because the
   * subscription set is derived rather than mutated — the event only says "the
   * answer has changed, ask again". `SWAP` re-points the anchor first and then
   * asks, and by construction the answer is *identical*, so it sends nothing.
   */
  #onRollEvent(kind: RollEventKind, marketId: string, atMs: number): void {
    switch (kind) {
      case 'PREWARM':
        this.#applyHolds(atMs)
        return
      case 'SWAP': {
        const { periodS, anchorS } = this.state.state
        this.#retarget(anchorS + periodS, atMs)
        return
      }
      case 'TEARDOWN':
        // The batched `unsubscribe` of §6.2's `T+74` row, and the store half of
        // the same teardown. `dropMarketDepth` leaves `market.{id}` alone: the
        // receipt is on screen until `T+81`.
        this.#applyHolds(atMs)
        this.stores?.dropMarketDepth(marketId)
        this.stores?.notifyAll()
        return
      case 'RETIRE':
        this.#applyHolds(atMs)
        this.stores?.dropMarket(marketId)
        this.stores?.notifyAll()
        return
      case 'WATCHDOG':
        void this.#watchdog(marketId)
        return
    }
  }

  /**
   * The roll's only permitted HTTP call, and the happy path must fire **zero**
   * of them (§6.5).
   *
   * Armed for every market and fired for almost none: by `resolve_eta_ms + 4s`
   * the `market.{id}` frame has normally already carried `RESOLVED`, so the
   * first thing this does is look at the market it was armed for and give up.
   * A watchdog that always fires is not a watchdog — it is a poll with a
   * jitter, and invariant 8 has a word for that.
   *
   * When it does fire it is **one batched reconcile**, not three waves: the
   * resolution, the balance and the positions go out together over the one
   * pooled HTTP/2 connection, jittered `U(0, 2000)ms` by the schedule that woke
   * it so a real backend stall does not become a stampede.
   */
  async #watchdog(marketId: string): Promise<void> {
    const stores = this.stores
    if (!stores) return
    const market = stores.marketFor(marketId).value as Record<string, unknown> | null
    // Resolved, settled, voided, refunded or cancelled: the answer arrived and
    // there is nothing to ask. This is the branch the happy path takes, sixty
    // times an hour, and it is why the criterion can say "the happy path fires
    // zero" rather than "the happy path fires one small one". A market with no
    // object at all stands down too, and a tab still holding it `OPEN` does not
    // — `watchdogReconciles` says why.
    if (!watchdogReconciles(market?.state)) return

    const requests: Promise<unknown>[] = [
      this.api.markets
        .getMarketResolution(marketId)
        .then((resolution) => {
          if (!resolution) return
          // Through the same `StoreSet.apply` path a frame takes: `market.{id}`
          // is authoritative for the outcome and the settlement price (§6.2
          // rule 4), and a second write path is a second opinion about which of
          // the two is newer.
          this.#applySnapshot(`market.${marketId}` as ChannelName, {
            ...(market ?? {}),
            state: 'RESOLVED',
            resolution,
          })
        })
        .catch(() => {
          /* the socket is still the primary path; a failed reconcile changes nothing */
        }),
    ]
    if (this.#privateChannels) {
      requests.push(
        this.api.portfolio
          .getBalance()
          .then((balance) => this.#applySnapshot('balance' as ChannelName, balance))
          .catch(() => {}),
        this.api.portfolio
          .listPositions()
          .then((page) => {
            const data = (page as { data?: unknown[] } | null)?.data
            this.#applySnapshot('positions' as ChannelName, {
              positions: Array.isArray(data) ? data : [],
            })
          })
          .catch(() => {}),
      )
    }
    await Promise.all(requests)
    stores.notifyAll()
  }

  /**
   * The asset switch (§5.5).
   *
   * One batched frame — the departing asset's depth channels out, the arriving
   * asset's in, at the **same anchor** — and one `GET /markets/current`. The ack
   * usually lands first and its in-band snapshot is the render source; the HTTP
   * response reconciles against it.
   *
   * `market.{eth N}` was already subscribed, so **the countdown does not jump
   * and the strike line does not blank**: only the depth panels transition, and
   * they transition inside one frame.
   */
  async switchAsset(asset: Asset): Promise<void> {
    const current = this.state.state
    if (current.asset === asset || current.phase !== 'READY') return
    if (!current.assets.includes(asset)) return

    // Keep the departing asset's ticker so the strip keeps its price and
    // switching back costs nothing.
    this.#retainedTicker = `ticker.${buildMarketId(seriesIdFor(current.asset), current.anchorS)}` as ChannelName

    const symbol = symbolFor(asset)
    this.#patch({ asset, symbol, seriesId: seriesIdFor(asset), marketId: buildMarketId(seriesIdFor(asset), current.anchorS) })
    this.state.notify()

    // The holds are per series, so switching asset at `T+50` must pre-warm the
    // *arriving* asset's successor rather than wait a minute for the next
    // window — and `#applyHolds` does that from the clock alone, because the
    // set is derived rather than accumulated. `reschedule` is the other half:
    // the watchdog's instant comes off the *predecessor's* `resolve_eta_ms`,
    // which has just become a different market's. §F21m, in one gesture.
    this.#applyHolds()
    this.#roll?.reschedule()

    try {
      const response = await this.api.markets.getCurrentMarkets({
        symbol: [symbol],
        include: [...STRIP_INCLUDE],
      })
      this.#reconcileStrip(response, symbol, current.anchorS, asset)
      this.state.notify()
    } catch {
      // The socket's in-band snapshot is the render source; the HTTP call is the
      // reconciliation. Losing it costs nothing the user can see.
    }
  }

  /**
   * Apply one `GET /markets/current` entry, and say what it actually carried.
   *
   * The return value is what makes `#resnapshotDepth` bounded: `include` is a
   * request, not a guarantee, and a venue that ignores it answers `200` with
   * three fields missing. Reporting which of the three landed is the difference
   * between "fetch the rest" and "fetch everything twice".
   */
  #reconcileStrip(
    response: unknown,
    symbol: Symbol_,
    anchorS: number,
    asset: Asset,
  ): { orderbook: boolean; trades: boolean; candles: boolean } {
    const carried = { orderbook: false, trades: false, candles: false }
    const entry = firstStripEntry(response, symbol)
    if (!entry) return carried
    const marketId = buildMarketId(seriesIdFor(asset), anchorS)
    for (const market of [entry.previous, entry.current, entry.next]) {
      const id = idOf(market)
      if (id) this.#applySnapshot(`market.${id}` as ChannelName, market)
    }
    if (entry.orderbook) {
      this.#applySnapshot(`book.${marketId}` as ChannelName, entry.orderbook)
      carried.orderbook = true
    }
    if (entry.trades) {
      this.#applySnapshot(`trades.${marketId}` as ChannelName, { trades: [...entry.trades].reverse() })
      carried.trades = true
    }
    if (entry.candles) {
      this.#applySnapshot(`candles.${marketId}.1s` as ChannelName, { candles: entry.candles })
      carried.candles = true
    }
    return carried
  }

  /**
   * Re-fetch whatever the strip did not inline — the book, the tape, the chart.
   *
   * §9.2.5 asks for the depth panels to be re-snapshotted after a gap that may
   * have spanned whole markets, and `include` is how that is meant to cost one
   * request rather than four. But `include` is a request: a venue that does not
   * implement it answers `200` with the three fields absent, and the reconcile
   * then repairs the strip and leaves the ladder, the tape and the chart holding
   * whatever they held before the outage. **That is not a wrong answer, it is
   * no answer**, and it is invisible from here — which is exactly why this
   * fallback exists rather than a comment saying the server should do better.
   *
   * Bounded three ways: only what was missing, only the market on screen, and
   * only on the two paths that call `reconcile` — reconnect and wake. On a
   * venue that honours `include` it issues nothing at all.
   *
   * `#snapshotFor` is the same REST-snapshot implementation gap recovery uses,
   * including the tape's newest-first → oldest-first reversal. A second one
   * would be a second place for the ladder to be assembled slightly differently.
   */
  async #resnapshotDepth(carried: { orderbook: boolean; trades: boolean; candles: boolean }): Promise<void> {
    const marketId = this.state.state.marketId
    if (!marketId) return
    const wanted: { kind: 'book' | 'trades' | 'candles'; channel: ChannelName }[] = [
      ...(carried.orderbook ? [] : [{ kind: 'book' as const, channel: `book.${marketId}` as ChannelName }]),
      ...(carried.trades ? [] : [{ kind: 'trades' as const, channel: `trades.${marketId}` as ChannelName }]),
      ...(carried.candles ?
        []
      : [{ kind: 'candles' as const, channel: `candles.${marketId}.1s` as ChannelName }]),
    ]
    if (wanted.length === 0) return

    await Promise.all(
      wanted.map(async ({ kind, channel }) => {
        const body = await this.#snapshotFor({ kind, marketId })
        // `null` is a failed or unavailable read and must not blank a panel:
        // the last known good values stay, exactly as they do under a gap.
        if (body != null) this.#applySnapshot(channel, body)
      }),
    )
  }

  /**
   * The swap at `T+60`, and the anchor follow it grew out of.
   *
   * Zero HTTP is not an economy, it is the exit criterion: market ids are
   * deterministic (D4), so the client already knows the next market's name and
   * has been receiving its `market.{id}` frames since cold load. Since Phase 7
   * it is **zero frames of any kind**: the pre-warm at `T+42+U(0,8)` subscribed
   * everything the new desired set names, and the holds keep everything the old
   * one named, so `setDesired` below diffs to the empty set and sends nothing.
   * The ticket re-targets, the ladder repaints from deltas against a snapshot it
   * has held for eighteen seconds, and no byte leaves the tab.
   *
   * The anchor is *not* re-derived from the clock here. The scheduler's `SWAP`
   * instant is `anchor + period` off the corrected clock, and `#applyHolds`
   * proves the new set matches; taking `anchorAt(now)` instead would skip a
   * market whole if a tab woke from sleep two minutes late, and skipping the
   * predecessor hold is how the resolution panel would go missing.
   */
  #retarget(anchorS: number, atMs: number = this.clock.now()): void {
    const { asset, periodS } = this.state.state
    // A tab that was asleep for minutes lands here once per missed boundary.
    // Correct the anchor to the clock's if it has fallen further behind than one
    // period, so the catch-up is one step rather than one step per minute.
    const current = anchorAt(Math.max(this.clock.now(), atMs), periodS)
    const next = anchorS >= current - periodS ? anchorS : current
    this.#patch({ anchorS: next, marketId: buildMarketId(seriesIdFor(asset), next) })
    this.#applyHolds(atMs)
  }

  // -------------------------------------------------------------------------

  #patch(next: Partial<RuntimeState>): void {
    this.state.set({ ...this.state.state, ...next })
  }
}

// ---------------------------------------------------------------------------

export interface DesiredChannelsInput {
  asset: Asset
  /** Every asset the venue runs (§F138); the rest of them are siblings. */
  assets?: readonly Asset[]
  /** The market currently on screen. */
  anchorS: number
  periodS: number
  /** What the roll holds beyond it, as anchors (`domain/roll.ts`). */
  holds: RollHolds
  /** The departing asset's `ticker`, retained across a switch (§5.5). */
  retainedTicker?: ChannelName
  privateChannels?: boolean
  /**
   * The tab has been hidden past `HIDDEN_DEPTH_DROP_MS` (§9.2.4).
   *
   * Drops `book`, `trades`, `ticker` and `candles` and **nothing else**:
   * `market.*`, `series.*`, `index.*`, `heartbeat` and every private channel
   * stay, so notifications and settlements still land and the tab title can
   * still count down. A hidden tab taking 124 frames/s in the last five seconds
   * of every minute is a battery bug; a hidden tab that stops hearing about its
   * own settlement is a product bug.
   */
  depthSuspended?: boolean
}

/**
 * Everything the tab wants subscribed, at one instant.
 *
 * A module function rather than a method because the phase's central claim is
 * about *two* of its outputs: the desired set at `T+60−ε` and the set at
 * `T+60+ε` must be **identical**, so that `setDesired` diffs to nothing and the
 * boundary — the instant 3,000 tabs may all place their first order (§6.0 Fact
 * 2) — costs no control frame at all. That is one `expect(a).toEqual(b)` if this
 * is a function and an integration test with a fake socket if it is not.
 *
 * ## The base set, and the channel §5.1 was missing
 *
 * `market.{other N}` and both `index.*` are here on cold load **on purpose**:
 * the asset switch must not blank the countdown or the strip price, and the
 * BTC/ETH segmented control shows both live prices without switching.
 *
 * **`market.{other N+1}` is the thirteenth channel, and §5.1's "twelve" is now
 * wrong** (register §F25). Both assets' *current* markets were in the base set
 * and only the focused asset's *successor* was, so at every boundary the
 * sibling's successor became new and the sibling's successor-of-the-successor
 * became stale — one subscribe and one unsubscribe, at exactly the instant
 * nothing may be negotiated. Subscribing the sibling's successor alongside the
 * focus's is what makes the two sets equal, and it costs one channel out of the
 * connection's two hundred.
 */
export function desiredChannels(input: DesiredChannelsInput): ChannelName[] {
  const { asset, anchorS, periodS, holds } = input
  const served = input.assets ?? ALL_ASSETS
  const siblings = served.filter((a) => a !== asset)
  const focus = buildMarketId(seriesIdFor(asset), anchorS)
  const marketChannel = (a: Asset, s: number): ChannelName =>
    `market.${buildMarketId(seriesIdFor(a), s)}` as ChannelName

  const channels = new Set<ChannelName>([
    'heartbeat',
    ...served.map((a) => `index.${symbolFor(a)}` as ChannelName),
    ...served.map((a) => `series.${seriesIdFor(a)}` as ChannelName),
    marketChannel(asset, anchorS),
    marketChannel(asset, anchorS + periodS),
    ...siblings.flatMap((a) => [marketChannel(a, anchorS), marketChannel(a, anchorS + periodS)]),
    // The focused asset's chat room (§F137). Named by the asset, never the
    // market, so it is in the set on both sides of every boundary and the roll
    // diffs to nothing — invariant 18 is why there is no room per market.
    `chat.${asset}` as ChannelName,
    // `candles.{focus}.1s` is in here and **nothing reads it today.** It was the
    // probability track the chart drew before §F19 made the chart the
    // underlying, and Phase 7's resolution panel was the expected consumer —
    // which it turned out not to be either, because the panel accumulates the
    // *index* over the settlement window rather than the market's own
    // probability track. If nothing takes it, drop it and amend §5.1 — but as a
    // decision, not by forgetting.
    ...(input.depthSuspended ? [] : depthChannelsFor(focus)),
  ])

  // Held beyond the focused market. Every asset for `market.*`, because the base
  // set carries both and a held anchor that only covered one would go stale on
  // the sibling at the boundary — the same asymmetry that made §5.1 twelve.
  for (const held of holds.markets) {
    channels.add(marketChannel(asset, held))
    for (const a of siblings) channels.add(marketChannel(a, held))
  }
  // Depth is the focused asset's only: the sibling's ladder is not on screen and
  // paying for four channels of a market nobody is looking at is the budget
  // §4.5 exists to spend somewhere else.
  if (!input.depthSuspended) {
    for (const held of holds.depth) {
      for (const c of depthChannelsFor(buildMarketId(seriesIdFor(asset), held))) channels.add(c)
    }
  }

  if (input.retainedTicker && !input.depthSuspended) channels.add(input.retainedTicker)
  // The six of docs/03 §5.3. Public channels are unaffected by their arrival and
  // by their departure — `setDesired` diffs the set, so signing in adds six and
  // touches nothing else.
  if (input.privateChannels) for (const c of PRIVATE_CHANNELS) channels.add(c)
  return [...channels]
}

// ---------------------------------------------------------------------------

interface StripEntry {
  symbol?: string
  previous?: unknown
  current?: unknown
  next?: unknown
  orderbook?: unknown
  trades?: unknown[]
  candles?: unknown[]
}

/** `performance.now()`. Not `Date.now()` — invariant 7 — and not `clock.now()`,
 *  which a re-sync may move by more than the intervals measured against it. */
function monoNow(): number {
  return globalThis.performance?.now?.() ?? 0
}

/** A paginated body's rows, or none. Every reconciliation read returns this
 *  shape and every one of them may have failed. */
/**
 * Did this settlement land inside the gap the tab is reconciling?
 *
 * `settled_at_ms` is the moment the venue paid it and is the only field on the
 * row that answers the question. A row missing it is **excluded** rather than
 * included: this feeds a sentence claiming the user was away for it, and the
 * safe direction for an unanswerable question is not to make the claim.
 */
function settledAfter(row: unknown, sinceMs: number): boolean {
  const at = (row as { settled_at_ms?: unknown } | null)?.settled_at_ms
  return typeof at === 'number' && at >= sinceMs
}

function rowsOf(page: unknown): unknown[] {
  const data = (page as { data?: unknown[] } | null)?.data
  return Array.isArray(data) ? data : []
}

/** The `data[]` row for one symbol out of a `GET /markets/current` response. */
export function firstStripEntry(response: unknown, symbol: string): StripEntry | null {
  const data = (response as { data?: unknown[] } | null)?.data
  if (!Array.isArray(data)) return null
  const match = data.find((row) => (row as StripEntry)?.symbol === symbol)
  return (match ?? data[0] ?? null) as StripEntry | null
}

function idOf(market: unknown): string | null {
  const id = (market as { market_id?: unknown } | null)?.market_id
  return typeof id === 'string' ? id : null
}

/**
 * `GET /series` + `GET /series/{id}/markets` in the shape `series.{id}` sends.
 *
 * The rail is fed by both transports and only one of them carries `markets[]`,
 * so something has to bridge. Doing it here, once, keeps `<SeriesRail>` reading
 * a single store rather than branching on which half of its data has arrived.
 */
export function railSnapshotFrom(
  result: BootResult,
  asset: Asset,
  anchorS: number,
  periodS: number,
): Record<string, unknown> | null {
  const seriesList = (result.public.series as { data?: unknown[] } | null)?.data
  const markets = (result.public.seriesMarkets as { data?: unknown[] } | null)?.data
  if (!Array.isArray(seriesList) && !Array.isArray(markets)) return null

  const seriesId = seriesIdFor(asset)
  const series = (Array.isArray(seriesList) ? seriesList : []).find(
    (s) => (s as { series_id?: string }).series_id === seriesId,
  ) as Record<string, unknown> | undefined

  return {
    series_id: seriesId,
    symbol: symbolFor(asset),
    underlying: asset.toUpperCase(),
    period_seconds: series?.period_seconds ?? periodS,
    twap_window_seconds: series?.twap_window_seconds ?? 10,
    markets_ahead: series?.markets_ahead ?? 3,
    current_market_id: buildMarketId(seriesId, anchorS),
    next_market_id: buildMarketId(seriesId, anchorS + periodS),
    markets: Array.isArray(markets) ? markets : [],
  }
}

// ---------------------------------------------------------------------------

/** The tab's runtime. One, like the socket it owns. */
let shared: Runtime | null = null

export function sharedRuntime(options?: RuntimeOptions): Runtime {
  shared ??= new Runtime(options)
  // Exposed for the browser suite, which has to assert on identity state that
  // has no unambiguous DOM representation — "the local key was dropped, and for
  // this reason" is a store fact, and reading it through a rendered string would
  // make the test a test of copy. A handle on an object the page already holds
  // adds no capability a user does not have via devtools.
  ;(globalThis as unknown as { __udRuntime?: Runtime }).__udRuntime = shared
  return shared
}

/** Tests only. */
export function __resetSharedRuntime(): void {
  shared?.stop()
  shared = null
}
